import { memo, useEffect, useRef } from "react";
import styles from "./entryPoint.module.scss";
import PageCard from "../../../components/PageCard/PageCard";
import ButtonShared from "../../../components/Button/Button";
import { useToast } from "../../../helpers/hooks";
import { CircularProgress } from "@mui/material";
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { TermsConditionReportPageProps } from "../../../interfaces/pages";

const POLLING_INTERVAL_MS = 4000;

const TermsConditionReport = ({
    termsConditionReport,
    generateTermsConditionReport,
    checkTermsConditionReport,
    cleanTermsConditionReport
}: TermsConditionReportPageProps) => {
    const toast = useToast();
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (termsConditionReport.fetching === "polling" && termsConditionReport.reportId) {
            intervalRef.current = setInterval(() => {
                checkTermsConditionReport(termsConditionReport.reportId!);
            }, POLLING_INTERVAL_MS);
        } else {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        }
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [termsConditionReport.fetching, termsConditionReport.reportId]);

    useEffect(() => {
        if (termsConditionReport.fetching === "ready" && termsConditionReport.downloadUrl) {
            const url = termsConditionReport.downloadUrl;
            cleanTermsConditionReport();
            fetch(url)
                .then(res => res.blob())
                .then(blob => {
                    const blobUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = "TyC.csv";
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(blobUrl);
                });
        }
    }, [termsConditionReport.fetching, termsConditionReport.downloadUrl]);

    useEffect(() => {
        if (termsConditionReport.fetching === "failed") {
            toast("Ha ocurrido un error al generar el reporte. Intentá de nuevo.");
            cleanTermsConditionReport();
        }
    }, [termsConditionReport.fetching]);

    const isLoading = termsConditionReport.fetching === "requesting" || termsConditionReport.fetching === "polling";

    return (
        <PageCard className={styles.container}>
            <div className={styles.header}>
                <h2 className={styles.title}>Reportes</h2>
                <p className={styles.subtitle}>Descargá reportes de aceptación de términos y condiciones</p>
            </div>
            <div className={styles.reportCard}>
                <div className={styles.row}>
                    <div className={styles.textGroup}>
                        <h3 className={styles.cardTitle}>Aceptación de Términos y Condiciones</h3>
                        <p className={styles.cardDescription}>
                            Genera un CSV con la última aceptación de términos y condiciones de cada usuario de la compañía. <br />
                            Incluye nombre, apellido, email, RUT, versión aceptada y fecha de aceptación.
                        </p>
                    </div>
                    <ButtonShared
                        className={styles.generateButton}
                        title={isLoading ? "Generando reporte..." : "Descargar Reporte"}
                        onPress={() => generateTermsConditionReport()}
                        disabled={isLoading}
                        icon={isLoading ? <CircularProgress size={16} color="inherit" /> : <CloudDownloadIcon />}
                        primary={true}
                        fontSize="small"
                    />
                </div>
            </div>
        </PageCard>
    );
};

export default memo(TermsConditionReport);
